import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useSupportChatFabLayout, getSupportChatFabEffectiveMaxRem } from '@/stores/supportChatFabLayout.store';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MessageCircle,
  X,
  Send,
  Loader2,
  Home,
  MessageSquare,
  HelpCircle,
  ChevronRight,
  Search,
  Sparkles,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { isAuthPath } from '@/lib/public-spa-paths';
import { isOnboardingHelpTopicId } from '@/pages/help/help-center-topics';
import {
  buildManagedFormationUrlMap,
  fetchPublicManagedHelpCategories,
  helpCenterApiRowsToCategories,
  type HelpCenterManagedCategoryDto,
} from '@/pages/help/help-center-managed.api';
import { translateHelpCategories } from '@/pages/help/translate-help-categories';
import { useHelpTopicLauncher } from '@/pages/help/useHelpTopicLauncher';
import type { UiLanguageCode } from '@/lib/ui-languages';
import { UI_LANGUAGES } from '@/lib/ui-languages';
import { getCookie } from '@/lib/cookies';

/** Aligné sur `EduFlowDesignerPage` : cookie + même bornes, pour le FAB pendant le lazy-load du designer. */
const DESIGNER_PROPERTIES_PANEL_WIDTH_COOKIE = 'designer_properties_panel_width';
const DESIGNER_PANEL_MIN_PX = 300;

function estimateDesignerRightPanelWidthPx(): number {
  if (typeof window === 'undefined') return DESIGNER_PANEL_MIN_PX;
  const vw = window.innerWidth;
  const maxWidth = Math.floor(vw / 3);
  const raw = getCookie(DESIGNER_PROPERTIES_PANEL_WIDTH_COOKIE);
  let w: number;
  if (raw == null || raw === '') {
    w = Math.max(DESIGNER_PANEL_MIN_PX, Math.floor((vw * 2) / 9));
  } else {
    const parsed = parseInt(raw, 10);
    w = Number.isFinite(parsed)
      ? parsed
      : Math.max(DESIGNER_PANEL_MIN_PX, Math.floor((vw * 2) / 9));
  }
  return Math.min(maxWidth, Math.max(DESIGNER_PANEL_MIN_PX, w));
}

type SupportMessageRow = {
  id: string;
  content: string;
  senderType: 'user' | 'admin' | 'system';
  createdAt: string;
};

type SupportConversationSession = {
  id: string;
  subject: string | null;
  status: string;
  messages: SupportMessageRow[];
};

type PublicChatSessionPayload = {
  conversationId: string;
  visitorToken: string;
  conversation: SupportConversationSession;
};

type VisitorIdentity = {
  firstName: string;
  lastName: string;
  email: string;
};

type SupportTab = 'home' | 'messages' | 'help';

function unwrapData<T>(raw: unknown): T {
  if (raw && typeof raw === 'object' && 'data' in raw) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

const VISITOR_CHAT_STORAGE_KEY = 'support_public_chat_session_v1';
const VISITOR_IDENTITY_STORAGE_KEY = 'support_public_chat_identity_v1';

const SUPPORT_API_LANG_SET = new Set<string>(UI_LANGUAGES.map((l) => l.code as UiLanguageCode));

/** Designer EduFlow : largeur panneau via `supportChatFabLayout.store` (comme le dashboard formateur). */
function isEduflowDesignerPath(pathname: string): boolean {
  return /^\/eduflow\/flows\/[^/]+\/designer(?:\/|$)/.test(pathname);
}

const SUPPORT_FAB_GAP_PX = 16;
const SUPPORT_FAB_SIZE_PX = 56;

/** Webinaire + player formation + podcast public / apprenant : ne pas afficher la bulle support Lyrra. */
function isImmersiveWebinarOrFlowPlayerPath(pathname: string): boolean {
  if (pathname.startsWith('/webinars/live/')) return true;
  if (pathname.startsWith('/learner/parcours/')) return true;
  if (pathname.startsWith('/parcours/player/')) return true;
  if (/^\/eduflow\/flows\/[^/]+\/play(?:\/|$)/.test(pathname)) return true;
  if (/^\/eduflow\/flows\/[^/]+\/public(?:\/|$)/.test(pathname)) return true;
  if (/^\/f\/[^/]+/.test(pathname)) return true;
  if (pathname === '/podcasts/public' || pathname.startsWith('/podcasts/public/')) return true;
  if (pathname === '/podcasts/learner' || pathname.startsWith('/podcasts/learner/')) return true;
  return false;
}

const PANEL_CARD =
  'rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900';
const PRIMARY_BTN =
  'rounded-xl bg-[color:var(--inst-player-primary,#0C1C38)] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[color:var(--inst-player-primary-hover,#19335E)] disabled:opacity-60';

export function SupportChatBubble() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [helpCenterRows, setHelpCenterRows] = useState<HelpCenterManagedCategoryDto[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const rows = await fetchPublicManagedHelpCategories();
      if (!cancelled) setHelpCenterRows(rows);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const managedFormationUrls = useMemo(() => buildManagedFormationUrlMap(helpCenterRows), [helpCenterRows]);

  const translatedHelpCategories = useMemo(() => {
    if (helpCenterRows.length > 0) {
      return helpCenterApiRowsToCategories(helpCenterRows, i18n.resolvedLanguage ?? i18n.language);
    }
    return translateHelpCategories(t);
  }, [helpCenterRows, t, i18n.language, i18n.resolvedLanguage]);

  const { openTopic, openingOnboardingPlayer, helpTopicModal } = useHelpTopicLauncher({
    managedFormationUrls,
    helpCategories: translatedHelpCategories,
  });
  const { isAuthenticated } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<SupportTab>('home');
  const [conversation, setConversation] = useState<SupportConversationSession | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState('');
  const [visitorIdentity, setVisitorIdentity] = useState<VisitorIdentity>({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [publicSession, setPublicSession] = useState<{ conversationId: string; visitorToken: string } | null>(
    null,
  );
  const [startingPublicChat, setStartingPublicChat] = useState(false);
  const [publicIdentityError, setPublicIdentityError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [helpCenterQuery, setHelpCenterQuery] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  /** Langue affichage : alignée sur react-i18next (sélecteur utilisateur), pas sur navigator. */
  const appLangKeyRaw = (i18n.resolvedLanguage ?? i18n.language ?? 'fr').split('-')[0].toLowerCase();
  const supportUiLang = SUPPORT_API_LANG_SET.has(appLangKeyRaw) ? appLangKeyRaw : 'fr';
  const localeForDates = i18n.resolvedLanguage ?? i18n.language ?? undefined;
  const tr = useCallback(
    (key: string, fallbackByLang: Partial<Record<string, string>> & { en: string }) => {
      const fallback = fallbackByLang[supportUiLang] ?? fallbackByLang.en;
      return t(key, { defaultValue: fallback });
    },
    [t, supportUiLang],
  );

  const hidden = useMemo(() => {
    if (location.pathname.startsWith('/admin')) return true;
    if (isImmersiveWebinarOrFlowPlayerPath(location.pathname)) return true;
    return isAuthPath(location.pathname);
  }, [location.pathname]);

  const designerChatInsetEnd = isEduflowDesignerPath(location.pathname);

  const trainerPanelStack = useSupportChatFabLayout((s) => s.stack);
  const designerRightPanelWidthPx = useSupportChatFabLayout((s) => s.designerRightPanelWidthPx);
  const trainerPanelMaxRem = useMemo(
    () => getSupportChatFabEffectiveMaxRem(trainerPanelStack),
    [trainerPanelStack],
  );

  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0,
  );
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /**
   * Dashboard formateur (`TrainerRightPanelHeader`) et designer EduFlow : bulle à gauche du bord du panneau droit,
   * ou en bas à gauche si la place manque (même règle que pour le formateur).
   */
  const rightPanelFabInsetStyle: CSSProperties | undefined = useMemo(() => {
    const vw = viewportWidth || (typeof window !== 'undefined' ? window.innerWidth : 0);
    let panelPx: number | null = null;
    if (designerChatInsetEnd) {
      panelPx = designerRightPanelWidthPx;
      if (panelPx == null || panelPx <= 0) {
        const fromCss = (() => {
          if (typeof window === 'undefined') return NaN;
          const raw = getComputedStyle(document.documentElement).getPropertyValue(
            '--lyrra-designer-right-panel-px',
          );
          const n = parseFloat(raw.replace('px', '').trim());
          return Number.isFinite(n) && n > 0 ? n : NaN;
        })();
        panelPx = Number.isFinite(fromCss) ? fromCss : estimateDesignerRightPanelWidthPx();
      }
    } else if (trainerPanelMaxRem != null) {
      const rootFont =
        typeof window !== 'undefined' ? parseFloat(getComputedStyle(document.documentElement).fontSize) || 16 : 16;
      panelPx = Math.min(vw, trainerPanelMaxRem * rootFont);
    }
    if (panelPx == null || panelPx <= 0) return undefined;
    const remaining = vw - panelPx;
    if (remaining >= SUPPORT_FAB_SIZE_PX + SUPPORT_FAB_GAP_PX) {
      return { insetInlineEnd: `${panelPx + SUPPORT_FAB_GAP_PX}px` };
    }
    return { insetInlineEnd: 'auto', insetInlineStart: `${SUPPORT_FAB_GAP_PX}px` };
  }, [designerChatInsetEnd, designerRightPanelWidthPx, trainerPanelMaxRem, viewportWidth]);

  const flatHelpTopics = useMemo(() => {
    const out: Array<{
      topicId: string;
      title: string;
      description: string;
      categoryTitle: string;
    }> = [];
    for (const cat of translatedHelpCategories) {
      for (const topic of cat.topics) {
        out.push({
          topicId: topic.id,
          title: topic.title,
          description: topic.description,
          categoryTitle: cat.title,
        });
      }
    }
    return out;
  }, [translatedHelpCategories]);

  const filteredHelpTopics = useMemo(() => {
    const q = helpCenterQuery.trim().toLowerCase();
    if (!q) return flatHelpTopics;
    return flatHelpTopics.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        item.categoryTitle.toLowerCase().includes(q),
    );
  }, [flatHelpTopics, helpCenterQuery]);

  const loadAuthenticatedSession = useCallback(async () => {
    setLoading(true);
    try {
      const raw = await api.get<unknown>('/support/chat/session');
      const data = unwrapData<SupportConversationSession | null>(raw);
      setConversation(data);
    } catch {
      setConversation(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPublicSession = useCallback(
    async (session: { conversationId: string; visitorToken: string }) => {
      setLoading(true);
      try {
        const raw = await api.get<unknown>('/support/chat/public/session', {
          conversationId: session.conversationId,
          visitorToken: session.visitorToken,
        });
        const data = unwrapData<SupportConversationSession | null>(raw);
        setConversation(data);
      } catch {
        setConversation(null);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (hidden || !open) return;
    if (isAuthenticated) {
      void loadAuthenticatedSession();
      return;
    }
    const rawSession = localStorage.getItem(VISITOR_CHAT_STORAGE_KEY);
    const rawIdentity = localStorage.getItem(VISITOR_IDENTITY_STORAGE_KEY);
    if (rawIdentity) {
      try {
        const identity = JSON.parse(rawIdentity) as VisitorIdentity;
        if (identity?.firstName && identity?.lastName && identity?.email) setVisitorIdentity(identity);
      } catch {
        // ignore bad local data
      }
    }
    if (!rawSession) {
      setPublicSession(null);
      setConversation(null);
      return;
    }
    try {
      const parsed = JSON.parse(rawSession) as { conversationId?: string; visitorToken?: string };
      if (parsed.conversationId && parsed.visitorToken) {
        const session = { conversationId: parsed.conversationId, visitorToken: parsed.visitorToken };
        setPublicSession(session);
        void loadPublicSession(session);
      } else {
        setPublicSession(null);
        setConversation(null);
      }
    } catch {
      setPublicSession(null);
      setConversation(null);
    }
  }, [hidden, isAuthenticated, loadAuthenticatedSession, loadPublicSession, open]);

  useEffect(() => {
    if (hidden || !open) return;
    const tmr = window.setInterval(
      () =>
        void (isAuthenticated
          ? loadAuthenticatedSession()
          : publicSession
            ? loadPublicSession(publicSession)
            : Promise.resolve()),
      10000,
    );
    return () => window.clearInterval(tmr);
  }, [hidden, open, isAuthenticated, loadAuthenticatedSession, loadPublicSession, publicSession]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [open, conversation?.messages?.length]);

  useEffect(() => {
    if (open) setTab('home');
  }, [open]);

  const canChat = isAuthenticated || Boolean(publicSession);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    setSendError(null);
    try {
      const raw = isAuthenticated
        ? await api.post<unknown>('/support/chat/messages', { content: text, uiLang: supportUiLang })
        : await api.post<unknown>('/support/chat/public/messages', {
            conversationId: publicSession?.conversationId,
            visitorToken: publicSession?.visitorToken,
            content: text,
            uiLang: supportUiLang,
          });
      const next = unwrapData<SupportConversationSession>(raw);
      setConversation(next);
      setDraft('');
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string'
          ? (e as { message: string }).message
          : tr('support.chat.error.sendFailed', {
              fr: "L'envoi a échoué. Réessayez ou rechargez la page.",
              en: 'Could not send your message. Try again or reload the page.',
              es: 'No se pudo enviar. Inténtalo de nuevo o recarga la página.',
              de: 'Senden fehlgeschlagen. Bitte erneut versuchen oder Seite neu laden.',
              tr: 'Mesaj gönderilemedi. Tekrar deneyin veya sayfayı yenileyin.',
            });
      setSendError(msg);
    } finally {
      setSending(false);
    }
  };

  const submitVisitorIdentity = async () => {
    const firstName = visitorIdentity.firstName.trim();
    const lastName = visitorIdentity.lastName.trim();
    const email = visitorIdentity.email.trim();
    if (!firstName || !lastName || !email) {
      setPublicIdentityError(
        tr('support.chat.identity.required', {
          fr: 'Veuillez renseigner prénom, nom et email.',
          en: 'Please provide first name, last name, and email.',
          es: 'Por favor, indique nombre, apellido y correo electrónico.',
          de: 'Bitte geben Sie Vorname, Nachname und E-Mail an.',
          it: 'Inserisci nome, cognome ed email.',
          pt: 'Preencha nome, sobrenome e e-mail.',
          ru: 'Укажите имя, фамилию и e-mail.',
          uk: "Вкажіть ім'я, прізвище та email.",
          lb: 'Gitt w.e.g. Virnumm, Numm an E-Mail un.',
          tr: 'Lütfen ad, soyad ve e-posta adresini girin.',
        }),
      );
      return;
    }
    setPublicIdentityError(null);
    setStartingPublicChat(true);
    try {
      const raw = await api.post<unknown>('/support/chat/public/start', { firstName, lastName, email });
      const data = unwrapData<PublicChatSessionPayload>(raw);
      const nextSession = { conversationId: data.conversationId, visitorToken: data.visitorToken };
      setPublicSession(nextSession);
      setConversation(data.conversation);
      const normalizedIdentity = { firstName, lastName, email };
      setVisitorIdentity(normalizedIdentity);
      localStorage.setItem(VISITOR_CHAT_STORAGE_KEY, JSON.stringify(nextSession));
      localStorage.setItem(VISITOR_IDENTITY_STORAGE_KEY, JSON.stringify(normalizedIdentity));
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string'
          ? (e as { message: string }).message
          : tr('support.chat.error.startFailed', {
              fr: 'Impossible de démarrer la conversation. Réessayez plus tard.',
              en: 'Could not start the conversation. Please try again later.',
              es: 'No se pudo iniciar la conversación. Inténtelo más tarde.',
              de: 'Konversation konnte nicht gestartet werden. Bitte später erneut versuchen.',
              tr: 'Görüşme başlatılamadı. Lütfen daha sonra tekrar deneyin.',
            });
      setPublicIdentityError(msg);
    } finally {
      setStartingPublicChat(false);
    }
  };

  const openMessages = () => setTab('messages');

  const goToFullHelpPage = () => {
    setOpen(false);
    navigate('/aide');
  };

  if (hidden) return null;

  const headerTitle = tr('support.chat.header.title', {
    fr: 'Envoyez-nous un message',
    en: 'Send us a message',
    es: 'Envíanos un mensaje',
    de: 'Schreiben Sie uns',
    tr: 'Bize mesaj gönderin',
  });
  const headerHint = tr('support.chat.header.hint', {
    fr: 'Nous répondons en général sous 12 h ouvrées.',
    en: 'We typically reply within 12 business hours.',
    es: 'Solemos responder en unas 12 horas laborables.',
    de: 'Wir antworten in der Regel innerhalb von 12 Geschäftsstunden.',
    tr: 'Genellikle 12 iş saati içinde yanıt veriyoruz.',
  });

  const supportFabHasCustomHorizontal = Boolean(rightPanelFabInsetStyle);
  const supportFabHorizontalStyle: CSSProperties | undefined = rightPanelFabInsetStyle;

  const panel = open && (
    <div
      className={cn(
        'fixed bottom-24 z-[10040] flex w-[min(100vw-2rem,400px)] max-h-[min(85vh,620px)] flex-col overflow-hidden rounded-[1.35rem] border border-slate-200/90 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-950',
        !supportFabHasCustomHorizontal && 'end-4 sm:end-6',
      )}
      style={supportFabHorizontalStyle}
      role="dialog"
      aria-label={tr('support.chat.title', { fr: 'Support', en: 'Support', tr: 'Destek' })}
    >
      {/* Bandeau dégradé + en-tête style panneau dashboard formateur */}
      <div
        className="shrink-0 bg-gradient-to-b from-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_18%,white)] to-white px-3 pb-2 pt-3 dark:from-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_35%,rgb(15_23_23))] dark:to-gray-950"
      >
        <div className={cn(PANEL_CARD, 'rounded-xl px-5 py-4 shadow-md')}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-semibold leading-tight text-gray-900 dark:text-white">{headerTitle}</h2>
              <p className="mt-1 text-xs leading-snug text-gray-500 dark:text-gray-400">{headerHint}</p>
            </div>
            <button
              type="button"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_14%,transparent)] text-[color:var(--inst-player-primary,#0C1C38)] transition hover:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_22%,transparent)] dark:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_22%,transparent)]"
              onClick={() => setOpen(false)}
              aria-label={tr('common.close', { fr: 'Fermer', en: 'Close', tr: 'Kapat' })}
            >
              <X className="h-5 w-5" strokeWidth={2.25} />
            </button>
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_6%,white)] dark:bg-gray-950/80">
        <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-2">
          {tab === 'home' && (
            <div className="space-y-3 pt-1">
              <button
                type="button"
                onClick={openMessages}
                className={cn(
                  PANEL_CARD,
                  'group flex w-full overflow-hidden text-start shadow-md transition hover:border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_35%,transparent)]',
                )}
              >
                <div className="flex flex-1 flex-col p-4">
                  <span className="mb-1.5 inline-flex w-fit items-center gap-1 rounded-full bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_14%,transparent)] px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-[color:var(--inst-player-primary,#0C1C38)] dark:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_24%,transparent)]">
                    <Sparkles className="h-3 w-3" />
                    {tr('support.chat.home.badge', { fr: 'Assistance', en: "We're here", es: 'Estamos aquí', de: 'Wir helfen', tr: 'Destek' })}
                  </span>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {tr('support.chat.home.cardTitle', {
                      fr: 'Une question sur Lyrra ?',
                      en: 'Need help with Lyrra?',
                      es: '¿Necesitas ayuda con Lyrra?',
                      de: 'Fragen zu Lyrra?',
                      tr: 'Lyrra hakkında bir sorunuz mu var?',
                    })}
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {tr('support.chat.home.cardBody', {
                      fr: "Notre équipe répond dans Messages ; les fiches ci-dessous sont les mêmes que sur la page Aide.",
                      en: 'Our team replies in Messages; the articles below are the same as on the Help page.',
                      es: 'Nuestro equipo responde en Mensajes; las fichas son las mismas que en la página de Ayuda.',
                      de: 'Unser Team antwortet unter Nachrichten; die Themen sind dieselben wie auf der Hilfeseite.',
                      tr: 'Ekibimiz Mesajlar bölümünde yanıt verir; aşağıdaki konular Yardım sayfasındakiyle aynıdır.',
                    })}
                  </p>
                </div>
                <div className="flex items-center border-s border-gray-100 px-3 dark:border-gray-800">
                  <ChevronRight className="h-5 w-5 text-[color:var(--inst-player-primary,#0C1C38)] transition group-hover:translate-x-0.5 rtl:-scale-x-100" />
                </div>
              </button>

              <div className={cn(PANEL_CARD, 'p-0 overflow-hidden')}>
                <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2.5 dark:border-gray-800">
                  <Search className="h-4 w-4 shrink-0 text-gray-400" />
                  <input
                    type="search"
                    value={helpCenterQuery}
                    onChange={(e) => setHelpCenterQuery(e.target.value)}
                    placeholder={t('help.searchPlaceholder')}
                    className="min-w-0 flex-1 border-0 bg-transparent py-1 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 dark:text-white"
                    aria-label={t('help.searchPlaceholder')}
                  />
                </div>
                <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                  {filteredHelpTopics.length === 0 ? (
                    <li className="px-3 py-8 text-center text-xs text-gray-500 dark:text-gray-400">
                      {t('help.noResults')}
                    </li>
                  ) : (
                    filteredHelpTopics.map((item) => (
                      <li key={item.topicId}>
                        <button
                          type="button"
                          disabled={openingOnboardingPlayer && isOnboardingHelpTopicId(item.topicId)}
                          onClick={() => openTopic(item.topicId)}
                          className="flex w-full items-start justify-between gap-2 px-3 py-3 text-start text-gray-800 transition hover:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_8%,transparent)] disabled:opacity-60 dark:text-gray-100 dark:hover:bg-gray-800/60"
                        >
                          <span className="min-w-0">
                            <span className="block text-[10px] font-semibold uppercase tracking-wide text-[color:var(--inst-player-primary,#0C1C38)]">
                              {item.categoryTitle}
                            </span>
                            <span className="mt-0.5 block text-sm font-medium leading-snug">{item.title}</span>
                            <span className="mt-1 line-clamp-2 text-xs text-gray-500 dark:text-gray-400">
                              {item.description}
                            </span>
                          </span>
                          <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-[color:var(--inst-player-primary,#0C1C38)] opacity-70" />
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              </div>
            </div>
          )}

          {tab === 'help' && (
            <div className="space-y-3 pt-1">
              <div className={cn(PANEL_CARD, 'p-4')}>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  {tr('support.chat.help.title', {
                    fr: 'Conseils rapides',
                    en: 'Quick tips',
                    es: 'Consejos rápidos',
                    de: 'Schnelle Tipps',
                    tr: 'Hızlı ipuçları',
                  })}
                </h3>
                <ul className="mt-3 space-y-2 text-xs text-gray-600 dark:text-gray-400">
                  <li className="flex gap-2">
                    <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[color:var(--inst-player-primary,#0C1C38)]" />
                    {tr('support.chat.help.tip1', {
                      fr: 'Décrivez votre problème et la page concernée (URL ou capture). Les fiches ouvrent les mêmes contenus que sur /aide.',
                      en: 'Describe the issue and the page (URL or screenshot). Topics open the same help as on /aide.',
                      de: 'Beschreiben Sie Ihr Problem und die Seite (URL oder Screenshot). Wie auf /aide.',
                      tr: 'Sorununuzu ve ilgili sayfayı açıklayın (URL veya ekran görüntüsü). Konular /aide sayfasındakiyle aynı içerikleri açar.',
                    })}
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[color:var(--inst-player-primary,#0C1C38)]" />
                    {tr('support.chat.help.tip2', {
                      fr: 'Pour les invités, nous devons votre email pour vous recontacter.',
                      en: 'As a guest, we need your email so we can follow up.',
                      tr: 'Misafir olarak, sizinle iletişime geçebilmemiz için e-posta adresinize ihtiyacımız var.',
                    })}
                  </li>
                </ul>
                <button
                  type="button"
                  onClick={goToFullHelpPage}
                  className="mt-4 w-full rounded-xl border border-gray-200 bg-white py-2.5 text-center text-sm font-semibold text-[color:var(--inst-player-primary,#0C1C38)] transition hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800/80 dark:hover:bg-gray-800"
                >
                  {t('help.title')} — <span className="font-normal opacity-90">/aide</span>
                </button>
              </div>
              <div className={cn(PANEL_CARD, 'p-0 overflow-hidden')}>
                <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2.5 dark:border-gray-800">
                  <Search className="h-4 w-4 shrink-0 text-gray-400" />
                  <input
                    type="search"
                    value={helpCenterQuery}
                    onChange={(e) => setHelpCenterQuery(e.target.value)}
                    placeholder={t('help.searchPlaceholder')}
                    className="min-w-0 flex-1 border-0 bg-transparent py-1 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 dark:text-white"
                    aria-label={t('help.searchPlaceholder')}
                  />
                </div>
                <p className="border-b border-gray-100 px-3 py-2 text-xs font-medium text-gray-500 dark:border-gray-800 dark:text-gray-400">
                  {t('help.description')}
                </p>
                <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                  {filteredHelpTopics.length === 0 ? (
                    <li className="px-4 py-8 text-center text-xs text-gray-500 dark:text-gray-400">
                      {t('help.noResults')}
                    </li>
                  ) : (
                    filteredHelpTopics.map((item) => (
                      <li key={`help-tab-${item.topicId}`}>
                        <button
                          type="button"
                          disabled={openingOnboardingPlayer && isOnboardingHelpTopicId(item.topicId)}
                          onClick={() => openTopic(item.topicId)}
                          className="flex w-full items-start justify-between gap-2 px-4 py-3 text-start hover:bg-gray-50 disabled:opacity-60 dark:hover:bg-gray-800/50"
                        >
                          <span className="min-w-0">
                            <span className="block text-[10px] font-semibold uppercase tracking-wide text-[color:var(--inst-player-primary,#0C1C38)]">
                              {item.categoryTitle}
                            </span>
                            <span className="mt-0.5 block text-sm font-medium text-gray-900 dark:text-white">
                              {item.title}
                            </span>
                            <span className="mt-1 line-clamp-2 text-xs text-gray-500 dark:text-gray-400">
                              {item.description}
                            </span>
                          </span>
                          <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-gray-400" />
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              </div>
            </div>
          )}

          {tab === 'messages' && (
            <div className="space-y-2 pt-1">
              <p className="px-1 text-xs text-gray-600 dark:text-gray-400">
                {tr('support.chat.subtitle', {
                  fr: 'Posez votre question : notre équipe vous répond ici.',
                  en: 'Ask your question: our team replies here.',
                  es: 'Haz tu pregunta: nuestro equipo te responde aqui.',
                  de: 'Stellen Sie Ihre Frage: Unser Team antwortet hier.',
                  it: 'Fai la tua domanda: il nostro team risponde qui.',
                  pt: 'Faça sua pergunta: nossa equipe responde aqui.',
                  ru: 'Задайте вопрос: наша команда ответит здесь.',
                  uk: 'Поставте запитання: наша команда відповість тут.',
                  lb: 'Stell deng Fro: eis Team äntwert hei.',
                  tr: 'Sorunuzu sorun: ekibimiz burada yanıt verir.',
                })}
              </p>

              {!isAuthenticated && !publicSession ? (
                <div className={cn(PANEL_CARD, 'p-4')}>
                  <p className="mb-3 text-xs text-gray-600 dark:text-gray-300">
                    {tr('support.chat.identity.title', {
                      fr: 'Pour vous recontacter, indiquez vos informations :',
                      en: 'Before chatting, please share your details:',
                      es: 'Antes de chatear, comparte tus datos:',
                      de: 'Vor dem Chat teilen Sie bitte Ihre Daten:',
                      it: 'Prima di chattare, inserisci i tuoi dati:',
                      pt: 'Antes de conversar, informe seus dados:',
                      ru: 'Перед началом чата укажите ваши данные:',
                      uk: 'Перед початком чату вкажiть вашi данi:',
                      lb: 'Ier mer chatten, gitt w.e.g. är Donnéeen un:',
                      tr: 'Sizinle iletişime geçebilmemiz için bilgilerinizi girin:',
                    })}
                  </p>
                  <div className="grid grid-cols-1 gap-2">
                    <input
                      type="text"
                      className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                      value={visitorIdentity.firstName}
                      onChange={(e) => setVisitorIdentity((prev) => ({ ...prev, firstName: e.target.value }))}
                      placeholder={tr('support.chat.identity.firstName', { fr: 'Prénom', en: 'First name', tr: 'Ad' })}
                    />
                    <input
                      type="text"
                      className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                      value={visitorIdentity.lastName}
                      onChange={(e) => setVisitorIdentity((prev) => ({ ...prev, lastName: e.target.value }))}
                      placeholder={tr('support.chat.identity.lastName', { fr: 'Nom', en: 'Last name', tr: 'Soyad' })}
                    />
                    <input
                      type="email"
                      className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                      value={visitorIdentity.email}
                      onChange={(e) => setVisitorIdentity((prev) => ({ ...prev, email: e.target.value }))}
                      placeholder={tr('support.chat.identity.email', { fr: 'Email', en: 'Email', tr: 'E-posta' })}
                    />
                    {publicIdentityError ? (
                      <p className="text-xs text-red-600 dark:text-red-400">{publicIdentityError}</p>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => void submitVisitorIdentity()}
                      disabled={startingPublicChat}
                      className={PRIMARY_BTN}
                    >
                      {startingPublicChat ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {tr('support.chat.identity.starting', { fr: 'Ouverture...', en: 'Starting...', tr: 'Başlatılıyor...' })}
                        </span>
                      ) : (
                        tr('support.chat.identity.submit', {
                          fr: 'Commencer la conversation',
                          en: 'Start conversation',
                          es: 'Iniciar conversacion',
                          de: 'Konversation starten',
                          it: 'Inizia conversazione',
                          pt: 'Iniciar conversa',
                          ru: 'Начать разговор',
                          uk: 'Почати розмову',
                          lb: 'Gespréich starten',
                          tr: 'Görüşmeyi başlat',
                        })
                      )}
                    </button>
                  </div>
                </div>
              ) : null}

              <div
                ref={listRef}
                className="flex max-h-[min(42vh,280px)] min-h-[140px] flex-col gap-3 overflow-y-auto px-1 py-2"
              >
                {loading && !conversation ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-[color:var(--inst-player-primary,#0C1C38)]" />
                  </div>
                ) : null}
                {(conversation?.messages ?? []).length === 0 && !loading && canChat ? (
                  <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                    {tr('support.chat.empty', {
                      fr: 'Écrivez un message pour démarrer la conversation.',
                      en: 'Write a message to start the conversation.',
                      es: 'Escribe un mensaje para empezar la conversacion.',
                      de: 'Schreiben Sie eine Nachricht, um die Unterhaltung zu starten.',
                      it: 'Scrivi un messaggio per iniziare la conversazione.',
                      pt: 'Escreva uma mensagem para iniciar a conversa.',
                      ru: 'Напишите сообщение, чтобы начать диалог.',
                      uk: 'Напишiть повідомлення, щоб почати розмову.',
                      lb: "Schreif eng Noriicht fir d'Gespréich unzefänken.",
                      tr: 'Görüşmeyi başlatmak için bir mesaj yazın.',
                    })}
                  </p>
                ) : null}
                {(conversation?.messages ?? []).map((m) => {
                  const fromSupport = m.senderType === 'admin' || m.senderType === 'system';
                  return (
                    <div
                      key={m.id}
                      className={cn(
                        'max-w-[88%] rounded-2xl px-3 py-2.5 text-sm shadow-sm',
                        fromSupport
                          ? 'me-auto border border-gray-100 bg-white text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100'
                          : 'ms-auto border border-transparent bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_16%,white)] text-gray-900 dark:bg-gray-800 dark:text-gray-100',
                      )}
                    >
                      <p
                        className={cn(
                          'text-[10px] uppercase tracking-wide text-gray-400',
                          !fromSupport && 'text-end',
                        )}
                      >
                        {fromSupport
                          ? tr('support.chat.fromSupport', { fr: 'Support', en: 'Support', tr: 'Destek' })
                          : tr('support.chat.fromYou', {
                              fr: 'Vous',
                              en: 'You',
                              es: 'Tu',
                              de: 'Sie',
                              it: 'Tu',
                              pt: 'Voce',
                              ru: 'Вы',
                              uk: 'Ви',
                              lb: 'Dir',
                              tr: 'Siz',
                            })}
                        {' · '}
                        {new Date(m.createdAt).toLocaleString(localeForDates)}
                      </p>
                      <p className={cn('mt-1 whitespace-pre-wrap', !fromSupport && 'text-end')}>{m.content}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {tab === 'messages' && canChat ? (
          <div className="shrink-0 border-t border-gray-200/90 bg-white/95 px-3 py-2 backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/95">
            {sendError ? (
              <p className="mb-2 text-xs text-red-600 dark:text-red-400" role="alert">
                {sendError}
              </p>
            ) : null}
            <div className="flex gap-2">
              <textarea
                rows={2}
                className="min-h-[44px] flex-1 resize-none rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm shadow-inner dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                placeholder={tr('support.chat.placeholder', {
                  fr: 'Votre message…',
                  en: 'Your message...',
                  es: 'Tu mensaje...',
                  de: 'Ihre Nachricht...',
                  it: 'Il tuo messaggio...',
                  pt: 'Sua mensagem...',
                  ru: 'Ваше сообщение...',
                  uk: 'Ваше повідомлення...',
                  lb: 'Är Noriicht...',
                  tr: 'Mesajınız...',
                })}
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                  if (sendError) setSendError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void send();
                  }
                }}
              />
              <button
                type="button"
                disabled={sending || !draft.trim()}
                className="flex h-10 w-10 shrink-0 items-center justify-center self-end rounded-xl bg-[color:var(--inst-player-primary,#0C1C38)] text-white shadow-md transition hover:brightness-105 disabled:opacity-50"
                onClick={() => void send()}
                aria-label={tr('support.chat.send', { fr: 'Envoyer', en: 'Send', tr: 'Gönder' })}
              >
                {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              </button>
            </div>
          </div>
        ) : null}

        <nav
          className="flex shrink-0 items-stretch justify-around gap-1 border-t border-gray-200 bg-white px-1 py-2 dark:border-gray-800 dark:bg-gray-900"
          aria-label={tr('support.chat.nav.aria', { fr: 'Navigation du widget support', en: 'Support widget navigation', tr: 'Destek bileşeni gezinmesi' })}
        >
          {(
            [
              { id: 'home' as const, Icon: Home, label: tr('support.chat.nav.home', { fr: 'Accueil', en: 'Home', de: 'Start', tr: 'Ana Sayfa' }) },
              {
                id: 'messages' as const,
                Icon: MessageSquare,
                label: tr('support.chat.nav.messages', { fr: 'Messages', en: 'Messages', de: 'Chats', tr: 'Mesajlar' }),
              },
              { id: 'help' as const, Icon: HelpCircle, label: tr('support.chat.nav.help', { fr: 'Aide', en: 'Help', de: 'Hilfe', tr: 'Yardım' }) },
            ] as const
          ).map(({ id, Icon, label }) => {
            const active = tab === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                className={cn(
                  'flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-lg px-2 py-1.5 text-[11px] font-medium transition',
                  active
                    ? 'text-[color:var(--inst-player-primary,#0C1C38)]'
                    : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200',
                )}
              >
                <span
                  className={cn(
                    'flex h-9 w-9 items-center justify-center rounded-full transition',
                    active
                      ? 'bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_18%,transparent)]'
                      : 'bg-gray-100 dark:bg-gray-800',
                  )}
                >
                  <Icon className={cn('h-5 w-5', active && 'text-[color:var(--inst-player-primary,#0C1C38)]')} />
                </span>
                <span className="truncate">{label}</span>
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );

  const launcher = (
    <button
      type="button"
      className={cn(
        'fixed bottom-6 z-[10039] flex h-14 w-14 items-center justify-center rounded-full bg-[color:var(--inst-player-primary,#0C1C38)] text-white shadow-lg transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_50%,transparent)]',
        !supportFabHasCustomHorizontal && 'end-4 sm:end-6',
      )}
      style={supportFabHorizontalStyle}
      onClick={() => setOpen((o) => !o)}
      aria-expanded={open}
      aria-label={tr('support.chat.open', {
        fr: 'Ouvrir le chat support',
        en: 'Open support chat',
        es: 'Abrir chat de soporte',
        de: 'Support-Chat offnen',
        it: 'Apri chat di supporto',
        pt: 'Abrir chat de suporte',
        ru: 'Открыть чат поддержки',
        uk: 'Вiдкрити чат підтримки',
        lb: 'Support-Chat opmaachen',
        tr: 'Destek sohbetini aç',
      })}
    >
      <MessageCircle className="h-7 w-7" />
    </button>
  );

  return createPortal(
    <>
      {launcher}
      {panel}
      {helpTopicModal}
    </>,
    document.body,
  );
}
